import {getLanguage} from '../i18n';
// LOOP PHONE AUTH R35 — verify/link phone with stable native error normalization.
// Web: Firebase JS SDK + reCAPTCHA.
// Native: @capacitor-firebase/authentication after a same-UID custom-token bridge.

import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
} from "firebase/auth";
import { firebaseAuth } from "../firebase";
import { httpRequestJson } from "../utils/nativeHttp";
import {
  getCurrentAuthIdToken,
  getLoopAuthPlatform,
} from "./socialAuth";
import {
  getManualAuth,
  persistManualAuth,
} from "../utils/manualAuth";

const FUNCTIONS_BASE =
  "https://us-central1-web-app-d9799.cloudfunctions.net";
const MINT_NATIVE_TOKEN_URL = `${FUNCTIONS_BASE}/mintPhoneLinkCustomTokenHttpR32`;
const SYNC_VERIFIED_PHONE_URL = `${FUNCTIONS_BASE}/syncVerifiedPhoneIdentityHttpR32`;

let pendingVerification = null;
let activeRecaptchaVerifier = null;
let nativeListenerHandles = [];
let verificationStartPromise = null;

function cleanPhone(value) {
  const raw = String(value || "").trim();
  if (!raw.startsWith("+")) return "";
  const digits = raw.replace(/\D/g, "");
  return digits.length >= 8 ? `+${digits}` : "";
}

function cleanCode(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

function timeoutError(label) {
  const error = new Error(label);
  error.code = label;
  return error;
}

function nativePhoneErrorText(event) {
  return [
    event?.code,
    event?.errorCode,
    event?.message,
    event?.error?.code,
    event?.error?.message,
  ]
    .filter(Boolean)
    .map((value) => String(value))
    .join(" ")
    .trim();
}

function normalizeNativePhoneError(event) {
  const originalCode = String(
    event?.code ||
    event?.errorCode ||
    event?.error?.code ||
    "",
  ).trim();

  const originalMessage = String(
    event?.message ||
    event?.error?.message ||
    "native-phone-verification-failed",
  ).trim();

  const raw = nativePhoneErrorText(event).toLowerCase();
  let semanticCode = originalCode || "phone-verification-failed";

  if (
    raw.includes("error code:39") ||
    raw.includes("error code: 39") ||
    raw.includes("17499") ||
    raw.includes("17010") ||
    raw.includes("too-many-requests") ||
    raw.includes("blocked all requests") ||
    raw.includes("unusual activity") ||
    raw.includes("try again later")
  ) {
    semanticCode = "phone-rate-limited";
  } else if (
    raw.includes("18002") ||
    raw.includes("not recognized by play store")
  ) {
    semanticCode = "phone-play-integrity-fallback";
  } else if (
    raw.includes("credential-already-in-use") ||
    raw.includes("phone-in-use") ||
    raw.includes("already exists")
  ) {
    semanticCode = "credential-already-in-use";
  } else if (raw.includes("provider-already-linked")) {
    semanticCode = "provider-already-linked";
  } else if (
    raw.includes("invalid-verification-code") ||
    raw.includes("invalid code")
  ) {
    semanticCode = "invalid-verification-code";
  } else if (
    raw.includes("invalid-phone-number") ||
    raw.includes("invalid phone")
  ) {
    semanticCode = "invalid-phone-number";
  }

  const error = new Error(originalMessage);
  error.code = semanticCode;
  error.nativeCode = originalCode;
  error.details = {
    code: originalCode,
    semanticCode,
    message: originalMessage,
  };
  return error;
}

async function withTimeout(promise, ms, label) {
  let timer = null;
  try {
    return await Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(timeoutError(label)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function postWithIdToken(url, idToken, data = {}) {
  if (!idToken) throw new Error("auth-token-missing");
  return httpRequestJson({
    url,
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
    data,
  });
}

function platformIsNative() {
  try {
    return Capacitor.isNativePlatform() === true;
  } catch (_) {
    return getLoopAuthPlatform() === "ios" || getLoopAuthPlatform() === "android";
  }
}

function persistNativeToken(idToken, nativeUser = null) {
  if (!idToken || getLoopAuthPlatform() !== "ios") return;
  const previous = getManualAuth() || {};
  persistManualAuth({
    ...previous,
    idToken,
    uid: nativeUser?.uid || previous.uid || null,
    email: nativeUser?.email || previous.email || "",
    displayName: nativeUser?.displayName || previous.displayName || "",
    provider: "phone",
    source: "ios-native-phone-link-r32",
    obtainedAt: new Date().toISOString(),
    expiresIn: "3600",
  });
}

async function removeNativeListeners() {
  const handles = nativeListenerHandles;
  nativeListenerHandles = [];
  await Promise.all(
    handles.map(async (handle) => {
      try {
        await handle?.remove?.();
      } catch (_) {}
    }),
  );
}

function clearRecaptcha() {
  try {
    activeRecaptchaVerifier?.clear?.();
  } catch (_) {}
  activeRecaptchaVerifier = null;
}

export async function cancelPhoneLinkVerification() {
  pendingVerification = null;
  clearRecaptcha();
  await removeNativeListeners();
}

async function syncVerifiedPhoneIdentity(idToken, source) {
  const payload = await postWithIdToken(
    SYNC_VERIFIED_PHONE_URL,
    idToken,
    { source: source || "profile_phone_verification_r32" },
  );
  if (!payload?.ok || !payload?.phoneVerified) {
    throw new Error(payload?.error || "phone-identity-sync-failed");
  }
  return payload;
}

async function ensureNativeSessionMatchesCurrentUid() {
  const currentIdToken = await getCurrentAuthIdToken({ forceRefresh: true });
  const mintResult = await postWithIdToken(
    MINT_NATIVE_TOKEN_URL,
    currentIdToken,
    { source: "profile_phone_link_r32" },
  );
  if (!mintResult?.ok || !mintResult?.customToken || !mintResult?.uid) {
    throw new Error(mintResult?.error || "native-phone-link-token-missing");
  }

  const signInResult = await withTimeout(
    FirebaseAuthentication.signInWithCustomToken({
      token: String(mintResult.customToken),
    }),
    30000,
    "native-phone-session-timeout",
  );
  const nativeUser = signInResult?.user || null;
  if (!nativeUser?.uid || String(nativeUser.uid) !== String(mintResult.uid)) {
    throw new Error("native-phone-session-uid-mismatch");
  }
  return nativeUser;
}

async function getFreshNativeToken() {
  const tokenResult = await FirebaseAuthentication.getIdToken({
    forceRefresh: true,
  });
  const idToken = String(tokenResult?.token || "");
  if (!idToken) throw new Error("native-phone-id-token-missing");
  const userResult = await FirebaseAuthentication.getCurrentUser().catch(() => ({ user: null }));
  persistNativeToken(idToken, userResult?.user || null);

  try {
    if (firebaseAuth?.currentUser?.uid === userResult?.user?.uid) {
      await firebaseAuth.currentUser.reload();
      await firebaseAuth.currentUser.getIdToken(true);
    }
  } catch (_) {}

  return idToken;
}

async function startNativeVerification(phoneE164) {
  await cancelPhoneLinkVerification();
  await ensureNativeSessionMatchesCurrentUid();
  await FirebaseAuthentication.setLanguageCode({ languageCode: getLanguage() }).catch(() => null);

  return withTimeout(
    new Promise(async (resolve, reject) => {
      let settled = false;
      const settle = async (fn, value, keepPending = false) => {
        if (settled) return;
        settled = true;
        if (!keepPending) await removeNativeListeners();
        fn(value);
      };

      try {
        const codeSentHandle = await FirebaseAuthentication.addListener(
          "phoneCodeSent",
          async (event) => {
            const verificationId = String(event?.verificationId || "");
            if (!verificationId) {
              await settle(reject, new Error("native-phone-verification-id-missing"));
              return;
            }
            pendingVerification = {
              platform: "native",
              phoneE164,
              verificationId,
              createdAt: Date.now(),
            };
            await settle(resolve, {
              ok: true,
              codeSent: true,
              autoCompleted: false,
              phoneE164,
            });
          },
        );

        const completedHandle = await FirebaseAuthentication.addListener(
          "phoneVerificationCompleted",
          async () => {
            try {
              const idToken = await getFreshNativeToken();
              const synced = await syncVerifiedPhoneIdentity(
                idToken,
                "native_phone_auto_verification_r32",
              );
              pendingVerification = null;
              await settle(resolve, {
                ok: true,
                codeSent: false,
                autoCompleted: true,
                verified: true,
                ...synced,
              });
            } catch (error) {
              await settle(reject, error);
            }
          },
        );

        const failedHandle = await FirebaseAuthentication.addListener(
          "phoneVerificationFailed",
          async (event) => {
            await settle(reject, normalizeNativePhoneError(event));
          },
        );

        nativeListenerHandles = [
          codeSentHandle,
          completedHandle,
          failedHandle,
        ].filter(Boolean);

        await FirebaseAuthentication.linkWithPhoneNumber({
          phoneNumber: phoneE164,
          timeout: 60,
        });
      } catch (error) {
        await settle(reject, error);
      }
    }),
    90000,
    "native-phone-code-timeout",
  );
}

async function startWebVerification(phoneE164, recaptchaContainerId) {
  await cancelPhoneLinkVerification();
  const currentUser = firebaseAuth?.currentUser || null;
  if (!currentUser) throw new Error("web-phone-link-current-user-missing");

  const containerId = String(recaptchaContainerId || "profile-phone-recaptcha");
  activeRecaptchaVerifier = new RecaptchaVerifier(
    firebaseAuth,
    containerId,
    {
      size: "invisible",
      "expired-callback": () => {
        pendingVerification = null;
      },
    },
  );
  await activeRecaptchaVerifier.render();

  firebaseAuth.languageCode = getLanguage();
  const provider = new PhoneAuthProvider(firebaseAuth);
  const verificationId = await withTimeout(
    provider.verifyPhoneNumber(phoneE164, activeRecaptchaVerifier),
    90000,
    "web-phone-code-timeout",
  );

  pendingVerification = {
    platform: "web",
    phoneE164,
    verificationId: String(verificationId || ""),
    createdAt: Date.now(),
  };
  if (!pendingVerification.verificationId) {
    throw new Error("web-phone-verification-id-missing");
  }

  return {
    ok: true,
    codeSent: true,
    autoCompleted: false,
    phoneE164,
  };
}

export async function startPhoneLinkVerification({
  phoneE164,
  recaptchaContainerId = "profile-phone-recaptcha",
} = {}) {
  if (verificationStartPromise) {
    return verificationStartPromise;
  }

  const clean = cleanPhone(phoneE164);
  if (!clean) throw new Error("invalid-phone-number");

  verificationStartPromise = platformIsNative()
    ? startNativeVerification(clean)
    : startWebVerification(clean, recaptchaContainerId);

  try {
    return await verificationStartPromise;
  } finally {
    verificationStartPromise = null;
  }
}

export async function syncCurrentVerifiedPhoneIdentity({
  source = "phone_link_recovery_r32",
} = {}) {
  let idToken = "";

  if (platformIsNative()) {
    await ensureNativeSessionMatchesCurrentUid();
    idToken = await getFreshNativeToken();
  } else {
    const currentUser = firebaseAuth?.currentUser || null;
    if (!currentUser) throw new Error("web-phone-link-current-user-missing");
    idToken = await currentUser.getIdToken(true);
  }

  return syncVerifiedPhoneIdentity(idToken, source);
}

async function confirmNativeVerification(code) {
  const session = pendingVerification;
  if (!session || session.platform !== "native" || !session.verificationId) {
    throw new Error("phone-verification-session-missing");
  }

  await withTimeout(
    FirebaseAuthentication.confirmVerificationCode({
      verificationId: session.verificationId,
      verificationCode: code,
    }),
    60000,
    "native-phone-confirm-timeout",
  );
  const idToken = await getFreshNativeToken();
  const synced = await syncVerifiedPhoneIdentity(
    idToken,
    "native_phone_code_verification_r32",
  );
  await cancelPhoneLinkVerification();
  return synced;
}

async function confirmWebVerification(code) {
  const session = pendingVerification;
  if (!session || session.platform !== "web" || !session.verificationId) {
    throw new Error("phone-verification-session-missing");
  }
  const currentUser = firebaseAuth?.currentUser || null;
  if (!currentUser) throw new Error("web-phone-link-current-user-missing");

  const credential = PhoneAuthProvider.credential(
    session.verificationId,
    code,
  );
  const result = await withTimeout(
    linkWithCredential(currentUser, credential),
    60000,
    "web-phone-confirm-timeout",
  );
  const linkedUser = result?.user || currentUser;
  const idToken = await linkedUser.getIdToken(true);
  const synced = await syncVerifiedPhoneIdentity(
    idToken,
    "web_phone_code_verification_r32",
  );
  await cancelPhoneLinkVerification();
  return synced;
}

export async function confirmPhoneLinkVerification({ code } = {}) {
  const clean = cleanCode(code);
  if (clean.length !== 6) throw new Error("invalid-verification-code");
  if (!pendingVerification) throw new Error("phone-verification-session-missing");
  return pendingVerification.platform === "native"
    ? confirmNativeVerification(clean)
    : confirmWebVerification(clean);
}

export function hasPendingPhoneLinkVerification() {
  return Boolean(pendingVerification?.verificationId);
}
